import React, { useMemo, useState, useEffect } from "react";
import { Link, useParams, withRouter, useLocation } from "react-router-dom";
import { connect } from 'react-redux'
import { useTable, usePagination, useSortBy, useExpanded, useResizeColumns, useFlexLayout, useRowSelect } from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from 'react-query'
import SortIcon from 'mdi-react/SortIcon';
import SortAscendingIcon from 'mdi-react/SortAscendingIcon';
import SortDescendingIcon from 'mdi-react/SortDescendingIcon';
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import { reducer, useEffectToDispatch, fetchApiData, initialState } from "@/shared/apiTableHelper"
import { useDispatch, flashError } from "@/shared/components/flash"
import ChangeStatusModal from "@/containers/Users/components/ChangeStatusModal"
import axios from "axios";
import {
    Container,
    Card,
    CardBody,
    Row,
    Col,
} from "reactstrap";

import { RECLAIM_POINTS_COLUMNS } from "./columns";
import AddProgramUserModal from '../AddProgramUserModal'
import ProgramBreadcrumb from '@/shared/components/ProgramBreadcrumb'
import { IndeterminateCheckbox } from "@/shared/components/form/IndeterminateCheckbox";
import BulkReclaimModal from "./BulkReclaimModal";
import BulkReclaimFilterForm from "./BulkReclaimFilterForm";

const queryClient = new QueryClient()

const DataTable = ({ program, organization, auth }) => {
    const location = useLocation();
    const flashDispatcher = useDispatch()
    const preselectedUsers = location?.state?.users || [];
    const [filter, setFilter] = useState({
        keyword: '',
        role_id: '',
        status: '',
        from: '',
        to: '',
        award_type: '',
        awarder: '',
        awardee: '',
        ...(preselectedUsers.length > 0 && { user_account_holder_id: preselectedUsers.map(u => u.account_holder_id) }),
    });
    const [useFilter, setUseFilter] = useState(false);
    const [trigger, setTrigger] = useState(Math.floor(Date.now() / 1000));
    const [isOpenAdd, setOpenAdd] = useState(false)
    const [user, setUser] = useState(null)
    const [isChangeStatusOpen, setChangeStatusOpen] = useState(false)
    const [isBulkReclaimOpen, setBulkReclaimOpen] = useState(false)
    const [selectedRows, setSelectedRows] = React.useState([]);

    const ReclaimPointsLinks = () => {
        return (
            <div>
                <span className="btn btn-sm btn-primary" onClick={() => toggleAllRowsSelected(true)}>Select All</span>
                <span className="btn btn-sm btn-primary" onClick={() => toggleAllRowsSelected(false)}>Deselect All</span>
                <span className="btn btn-sm btn-primary" onClick={toggleBulkReclaim}>Reclaim</span>
            </div>
        )
    }

    const toggleAdd = () => {
        setOpenAdd(prevState => !prevState)
    }

    const toggleChangeStatus = () => {
        setChangeStatusOpen(prevState => !prevState)
    }

    const toggleBulkReclaim = () => {
        if (!selectedRows.length) {
            flashError(flashDispatcher, 'Please select at least one award to reclaim.')
            return
        }
        setBulkReclaimOpen(prevState => !prevState)
    }

    let reclaim_points_columns = [
        {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <div className="d-flex ml-1">
                    <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                </div>
            ),
            Cell: ({ row }) => (
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            ),
            width: 100,
        },
        ...RECLAIM_POINTS_COLUMNS,
    ]

    let columns = useMemo(() => reclaim_points_columns, [])

    const defaultColumn = React.useMemo(
        () => ({
            minWidth: 30,
            width: 150,
            maxWidth: 400,
        }),
        []
    )

    const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger }, dispatch] = React.useReducer(reducer, initialState);

    const apiUrl = `organization/${organization.id}/program/${program.id}/bulk-reclaim-list`

    const { isLoading, error, data, isSuccess } = useQuery(
        ['users', queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
        () => fetchApiData(
            {
                url: apiUrl,
                page: queryPageIndex,
                size: queryPageSize,
                filter,
                sortby: queryPageSortBy,
                trigger: queryTrigger
            }),
        {
            keepPreviousData: true,
            staleTime: Infinity
        }
    );

    const totalPageCount = Math.ceil(totalCount / queryPageSize)
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        pageCount,
        pageOptions,
        gotoPage,
        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        setPageSize,
        toggleAllRowsSelected,
        selectedFlatRows,
        state: { pageIndex, pageSize, sortBy },
    } = useTable({
        columns,
        data: useMemo(() => (data ? data?.results : []), [data]),
        initialState: {
            pageIndex: queryPageIndex,
            pageSize: queryPageSize,
            sortBy: queryPageSortBy,
        },
        manualPagination: true,
        pageCount: data ? totalPageCount : 0,
        autoResetSortBy: false,
        autoResetExpanded: false,
        autoResetPage: false,
        defaultColumn,

    },
        useSortBy,
        useExpanded,
        usePagination,
        useResizeColumns,
        useFlexLayout,
        useRowSelect
    );

    const manualPageSize = []

    useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger });

    useEffect(() => {
        if (selectedFlatRows) {
            const allSelcetedRows = selectedFlatRows.map((row) => row.original);
            setSelectedRows(allSelcetedRows);
        }
    }, [selectedFlatRows]);

    if (error) {
        return <p>Error: {JSON.stringify(error)}</p>;
    }

    if (isLoading) {
        return <p>Loading...</p>;
    }

    const handleFilterChange = (newFilter) => {
        setFilter(prevFilter => ({
            ...prevFilter,
            keyword: newFilter.keyword,
            role_id: newFilter.role_id,
            status: newFilter.status,
            from: newFilter.from,
            to: newFilter.to,
            award_type: newFilter.award_type,
            awarder: newFilter.awarder,
            awardee: newFilter.awardee,
        }));
        setUseFilter(true);
    };


    if (isSuccess)
        return (
            <>
                <div className='table react-table'>
                    <BulkReclaimFilterForm
                        onClickFilterCallback={handleFilterChange}
                        filter={filter}
                        organization={organization}
                        program={program}
                    />
                    <div className="pb-4 d-flex align-items-center justify-content-between">
                        <ReclaimPointsLinks />
                        <span
                            style={{ maxWidth: '200px' }}
                            className="btn btn-primary account__btn account__btn--small"
                            onClick={toggleAdd}
                        >Add Program User</span>
                    </div>
                    <AddProgramUserModal organization={organization} program={program} isOpen={isOpenAdd} setOpen={setOpenAdd} toggle={toggleAdd} setTrigger={setTrigger} />
                    {user && <ChangeStatusModal isOpen={isChangeStatusOpen} setOpen={setChangeStatusOpen} toggle={toggleChangeStatus} setTrigger={setTrigger} user={user} />}
                    {!!selectedRows.length && <BulkReclaimModal isOpen={isBulkReclaimOpen} setOpen={setBulkReclaimOpen} toggle={toggleBulkReclaim} setTrigger={setTrigger} {...{ selectedRows, program }} />}

                    <table {...getTableProps()} className="table">
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                            {column.render('Header')}
                                            {column.isSorted ? <Sorting column={column} /> : ''}
                                            <div
                                                {...column.getResizerProps()}
                                                className={`resizer ${column.isResizing ? 'isResizing' : ''
                                                    }`}
                                            />
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="table table--bordered" {...getTableBodyProps()}>
                            {page.map(row => {
                                prepareRow(row);
                                const subCount = (row.id.match(/\./g) || []).length
                                return (
                                    <tr {...row.getRowProps()}>
                                        {
                                            row.cells.map(cell => {
                                                const paddingLeft = subCount * 20
                                                return <td {...cell.getCellProps()}><span style={cell.column.Header === '#' ? { paddingLeft: `${paddingLeft}px` } : null}>{cell.render('Cell')}</span></td>
                                            })
                                        }
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
                {(rows.length > 0) && (
                    <>
                        <ReactTablePagination
                            page={page}
                            gotoPage={gotoPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageSize={pageSize}
                            pageIndex={pageIndex}
                            pageCount={pageCount}
                            setPageSize={setPageSize}
                            manualPageSize={manualPageSize}
                            dataLength={totalCount}
                        />
                        <div className="pagination justify-content-end mt-2">
                            <span>
                                Go to page:{' '}
                                <input
                                    type="number"
                                    value={pageIndex + 1}
                                    onChange={(e) => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        gotoPage(page);
                                    }}
                                    style={{ width: '100px' }}
                                />
                            </span>{' '}
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                )}
            </>
        )
}

const Sorting = ({ column }) => (
    <span className="react-table__column-header sortable">
        {column.isSortedDesc === undefined ? (
            <SortIcon />
        ) : (
            <span>
                {column.isSortedDesc
                    ? <SortAscendingIcon />
                    : <SortDescendingIcon />}
            </span>
        )}
    </span>
);

const TableWrapper = ({ program, organization, auth }) => {
    return (
        <QueryClientProvider client={queryClient}>
            <DataTable {...{ program, organization, auth }} />
        </QueryClientProvider>
    )
}

const BulkReclaim = ({ organization, auth }) => {
    const { programId } = useParams();
    const [program, setProgram] = useState(null);
    const fetchProgramData = async (organization) => {
        try {
            const response = await axios.get(`/organization/${organization.id}/program/${programId}`);
            setProgram(response.data)
        } catch (e) {
            throw new Error(`API error:${e?.message}`);
        }
    };
    useEffect(() => {
        if (organization) {
            fetchProgramData(organization)
        }
    }, [organization])

    if (!program?.id || !organization?.id) {
        return 'Loading...'
    }
    return (
        <Container className="dashboard">
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Program Users Reclaim Points</h3>
                    <h3 className="page-subhead subhead"><Link className="" to="/">Home</Link> / <Link className="" to="/program">Programs</Link> <ProgramBreadcrumb program={program} linkSelf={true} /> / Users </h3>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Card>
                        <CardBody>
                            {program && organization && <TableWrapper {...{ program, organization, auth }} />}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}
export default withRouter(connect((state) => ({
    organization: state.organization, auth: state.auth
}))(BulkReclaim));

